import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import {
  AutocompleteInput,
  Button,
  Identifier,
  ReferenceInput,
  required,
  SaveButton,
  SelectInput,
  SimpleForm,
  useNotify,
} from "react-admin";
import { FieldValues, SubmitHandler } from "react-hook-form";
import { getRawUrlBase, SiteResourcesEnum } from "shared/enums/resourceMappings";
import { arrayToChoices } from "shared/utils/reactAdminHelpers";
import { ChoicesType, ChronicleTypeEnum, GenerationTabFormProps } from "./types";

const TriggerDagForm = ({ initialFormValues: { chronicle_type, jbox_type }, resetForm }: GenerationTabFormProps) => {
  const isPerformance = chronicle_type === ChronicleTypeEnum.Performance;
  const notify = useNotify();
  const [choices, setchoices] = useState<{ type: ChoicesType; chronicleFilename: ChoicesType; lot: ChoicesType }>({
    type: [],
    chronicleFilename: [],
    lot: [],
  });

  const getEnum = useCallback((element: { schema: { enum: any[] } }): Identifier[] => element?.schema?.enum || [], []);

  const getGenerationDetails = useCallback(async () => {
    try {
      const { data } = await axios({
        method: "get",
        url: `${getRawUrlBase(SiteResourcesEnum.Chronicles)}/performance/details`,
        params: {
          chronicle_type,
          jbox_type,
        },
      });

      if (data && data.params) {
        const { type, chronicle_filename, lot } = data.params;
        setchoices({
          type: arrayToChoices(getEnum(type)),
          chronicleFilename: arrayToChoices(getEnum(chronicle_filename)),
          lot: arrayToChoices(getEnum(lot)),
        });
      }
    } catch (error) {
      console.error("error:", error);
    }
  }, [getEnum, chronicle_type, jbox_type]);

  useEffect(() => {
    getGenerationDetails();
  }, [getGenerationDetails]);

  const handleSubmit: SubmitHandler<FieldValues> = useCallback(
    async (values) => {
      if (!values.site_id) {
        notify("Please select a site", { type: "error" });
      } else {
        const hiddenInfos = {
          prod: process.env.REACT_APP_ENVIRONNEMENT === "production",
          chronicle_type,
        };
        const data = { ...values, ...hiddenInfos };

        try {
          const { status } = await axios({
            method: "post",
            url: `${getRawUrlBase(SiteResourcesEnum.Chronicles)}/performance/report/generation`,
            data,
          });
          if (status >= 200 && status < 300) {
            notify("Succefully started chronicle generation", { type: "success" });
          }
        } catch (error) {
          notify("There was a problem starting chronicle generation", { type: "error" });
          console.error("error:", error);
        }
      }
    },
    [notify, chronicle_type],
  );

  return (
    <SimpleForm
      onSubmit={handleSubmit}
      title="Performance Generation"
      toolbar={
        <>
          <SaveButton label="generate report" />
          <Button onClick={resetForm} label="reset " variant="contained" size="medium" sx={{ ml: 2 }} />
        </>
      }
      mode="onBlur"
    >
      <ReferenceInput source="site_id" reference={SiteResourcesEnum.Sites}>
        <AutocompleteInput
          optionText={({ id, name }: any) => {
            return `${id} - ${name}`;
          }}
          validate={[required()]}
          fullWidth
        />
      </ReferenceInput>
      <SelectInput source="type" choices={choices.type} validate={[required()]} fullWidth />
      {isPerformance && <SelectInput source="lot" choices={choices.lot} validate={[required()]} fullWidth />}
      <SelectInput source="chronicle_filename" choices={choices.chronicleFilename} validate={[required()]} fullWidth />
    </SimpleForm>
  );
};

export default TriggerDagForm;
